import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { fmtClock, trimCheck, savedTrim } from '@/lib/videoManageState';

// LIVE-VIDEO-MANAGEMENT-1 · 재생 중인 버전 기준 앞뒤 자르기 · 저장 시 서버가 원본 녹화 시간축으로 변환.
export const VideoTrimEditor = ({ v, onSaved, disabled }) => {
  const dur = Number(v.playable?.duration_sec || 0);
  const ref = useRef(null);
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);
  const saved = savedTrim(v);
  const [a, setA] = useState(saved.start);
  const [b, setB] = useState(saved.end);
  const [busy, setBusy] = useState(false);
  const stale = v.video?.status === 'STALE';
  useEffect(() => {
    setA(saved.start); setB(saved.end); setUrl(null); setErr(null);
    api.get(`/seller/product-videos/${v.segment_id}/play-url`).then(({ data }) => setUrl(data.url))
      .catch(() => setErr('미리보기를 불러오지 못했습니다.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.segment_id, dur, saved.start, saved.end]);
  const same = Math.abs(a - saved.start) < 0.05 && Math.abs(b - saved.end) < 0.05;
  const bad = trimCheck(a, b, dur) || (stale && same ? '이미 저장된 수정 구간입니다 · 아래 \'수정본 생성\'을 눌러 v2를 만드세요.' : null);
  const here = () => Math.round((ref.current?.currentTime || 0) * 10) / 10;
  const preview = () => { const el = ref.current; if (!el) return; el.currentTime = a; el.play().catch(() => {}); };
  const onTime = (e) => { if (e.currentTarget.currentTime >= b) e.currentTarget.pause(); };
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/seller/broadcasts/${v.schedule_id}/product-segments/${v.segment_id}`, { trim: { start: a, end: b } });
      toast.success('수정 구간을 저장했습니다 · 수정본 생성 후 교체됩니다 (기존 영상 유지).');
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d === 'outside_recording' ? '원본 녹화 범위를 벗어났습니다.' : '구간 저장 실패 · 기존 영상이 유지됩니다.');
    } finally { setBusy(false); }
  };
  const num = 'w-20 px-2 py-1 rounded border border-black/15 dark:border-white/20 bg-transparent text-sm';
  return (
    <div className="space-y-2 p-2 rounded-lg border border-black/10 dark:border-white/10" data-testid="live-video-trim-editor">
      <p className="text-xs font-bold">앞뒤 구간 편집 <span className="font-normal text-gray-500">(v{v.playable?.version} 기준 · 원본 {fmtClock(dur)})</span></p>
      {err && <p className="text-xs text-red-600" data-testid="live-video-trim-preview-error">{err}</p>}
      {url && <video ref={ref} src={url} controls playsInline onTimeUpdate={onTime} className="block mx-auto max-h-[40vh] max-w-full rounded bg-black" data-testid="live-video-trim-preview" />}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <label className="space-y-1">시작(초)
          <div className="flex gap-1"><input type="number" step="0.1" min="0" max={dur} value={a} onChange={(e) => setA(Number(e.target.value))} className={num} data-testid="live-video-trim-start" />
            <button type="button" onClick={() => setA(here())} className="px-2 rounded border text-[11px]" data-testid="live-video-trim-set-start">현재 위치</button></div>
        </label>
        <label className="space-y-1">종료(초)
          <div className="flex gap-1"><input type="number" step="0.1" min="0" max={dur} value={b} onChange={(e) => setB(Number(e.target.value))} className={num} data-testid="live-video-trim-end" />
            <button type="button" onClick={() => setB(here())} className="px-2 rounded border text-[11px]" data-testid="live-video-trim-set-end">현재 위치</button></div>
        </label>
      </div>
      {stale && <p className="text-xs text-amber-700" data-testid="live-video-trim-saved">저장된 수정 구간: {fmtClock(saved.start)}~{fmtClock(saved.end)} ({fmtClock(saved.end - saved.start)}) · 아직 v2 미생성</p>}
      <p className="text-xs" data-testid="live-video-trim-summary">변경 전 0:00.0~{fmtClock(dur)} ({fmtClock(dur)}) → 변경 후 {fmtClock(a)}~{fmtClock(b)} <b>({fmtClock(Math.max(0, b - a))})</b></p>
      {bad && <p className="text-xs text-amber-700" data-testid="live-video-trim-error">{bad}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={!url} onClick={preview} className="px-3 py-1.5 rounded-full border text-xs" data-testid="live-video-trim-preview-btn">선택 구간 미리보기</button>
        <button type="button" onClick={() => { setA(saved.start); setB(saved.end); }} className="px-3 py-1.5 rounded-full border text-xs" data-testid="live-video-trim-reset">수정 취소</button>
        <button type="button" disabled={!!bad || busy || disabled} onClick={save} className="px-3 py-1.5 rounded-full bg-[#FF3B30] text-white text-xs font-bold disabled:opacity-30" data-testid="live-video-trim-save">{busy ? '저장 중…' : '수정 구간 저장'}</button>
      </div>
    </div>
  );
};
